/*
  Chifles cart bridge — native Shopify Ajax Cart API only.
  Rewritten from readymag-source/extracted/home/inline-script-05.js, which used
  the Storefront GraphQL API plus a localStorage cart. All of that is gone:
  no access token, no GraphQL, no localStorage. The server cart is truth.

  Public API (window.ChiflesCart):
    addItem(variantId, qty[, sellingPlanId]) -> POST /cart/add.js
    updateQuantity(lineKey, qty)   -> POST /cart/change.js ({id: lineKey})
    removeItem(lineKey)            -> POST /cart/change.js with quantity 0
    clear()                        -> POST /cart/clear.js
    getCart()                      -> GET /cart.js (async; prices in CENTS)
    openDrawer() / closeDrawer()
    checkout()                     -> window.location.href = '/checkout'

  After every mutation: re-fetch /cart.js (change/clear already return the
  cart), re-render the drawer, call window.updateChiflesCartBadge(item_count),
  and dispatch CustomEvent('chiflesCartChange', {detail: cart}).
*/
(function () {
  'use strict';

  var cart = null; // last known server cart (the /cart.js JSON shape)

  /* ----------------------------- helpers ----------------------------- */

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function thumbUrl(src) {
    if (!src) return null;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=120';
  }

  function fetchJson(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return {}; }).then(function (body) {
          var msg = (body && (body.description || body.message)) || ('HTTP ' + r.status);
          throw new Error(msg);
        });
      }
      return r.json();
    });
  }

  function post(url, payload) {
    return fetchJson(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  /* ------------------------- cart state plumbing ---------------------- */

  function broadcast(c) {
    cart = c;
    if (typeof window.updateChiflesCartBadge === 'function') {
      window.updateChiflesCartBadge(c.item_count || 0);
    }
    window.dispatchEvent(new CustomEvent('chiflesCartChange', { detail: c }));
    renderDrawer(c);
    return c;
  }

  function getCart() {
    return fetchJson('/cart.js').then(function (c) {
      cart = c;
      return c;
    });
  }

  function refresh() {
    return fetchJson('/cart.js').then(broadcast);
  }

  /* ----------------------------- mutations ---------------------------- */

  function addItem(variantId, quantity, sellingPlanId) {
    var line = { id: Number(variantId), quantity: quantity || 1 };
    // Subscribe and Save (Recharge, 2026-10-21): the product page passes the chosen
    // selling plan; every other caller omits it and buys one-time as before.
    if (sellingPlanId) line.selling_plan = Number(sellingPlanId);
    return post('/cart/add.js', {
      items: [line]
    })
      .then(refresh) // /cart/add.js returns only the added lines, not the cart
      // Round 6 (Amaranta): "clicking 'Add 24-Pack to Cart' adds the product but does
      // not open the cart drawer. It should automatically slide open the side cart
      // drawer whenever a product is added."
      //
      // Done HERE rather than in the click handlers she happened to name. She reported
      // it on the flavour category pages and Shop All, but those are only two of the
      // call sites: cf-grid-original, -sweet, -spicy, -lime and -garlic, cf-shop-all,
      // cf-product and cf-flavor-carousel all reach the cart through this one function.
      // Fixing the two surfaces named would have left six others silently behaving the
      // old way and looking fixed. Every path that adds a line now opens the drawer,
      // which is also what she actually asked for ("whenever a product is added").
      //
      // Sequenced after refresh, not beside it, so the drawer paints the server's cart
      // rather than the pre-add one: /cart/add.js returns only the added lines, and
      // openDrawer re-renders from `cart`, which refresh is what populates.
      .then(function (c) { openDrawer(); return c; })
      .catch(function (err) {
        console.error('[chifles cart] add failed:', err.message);
        refresh(); // re-sync whatever the server actually holds
        throw err;
      });
  }

  // lineKey is item.key from /cart.js (stable per line, survives duplicate variants)
  function updateQuantity(lineKey, quantity) {
    return post('/cart/change.js', { id: lineKey, quantity: quantity })
      .then(broadcast) // /cart/change.js returns the full updated cart
      .catch(function (err) {
        console.error('[chifles cart] change failed:', err.message);
        refresh();
        throw err;
      });
  }

  function removeItem(lineKey) {
    return updateQuantity(lineKey, 0);
  }

  function clear() {
    return post('/cart/clear.js', {})
      .then(broadcast) // /cart/clear.js returns the emptied cart
      .catch(function (err) {
        console.error('[chifles cart] clear failed:', err.message);
        refresh();
        throw err;
      });
  }

  /* ------------------------------ drawer ------------------------------ */

  function drawerEl(id) { return document.getElementById(id); }

  function openDrawer() {
    if (cart) renderDrawer(cart);
    refresh(); // repaint from the live server cart in the background
    var backdrop = drawerEl('cartDrawerBackdrop');
    var drawer = drawerEl('cartDrawer');
    if (backdrop) backdrop.classList.add('open');
    if (drawer) drawer.classList.add('open');
  }

  function closeDrawer() {
    var backdrop = drawerEl('cartDrawerBackdrop');
    var drawer = drawerEl('cartDrawer');
    if (backdrop) backdrop.classList.remove('open');
    if (drawer) drawer.classList.remove('open');
  }

  function checkout() {
    window.location.href = '/checkout';
  }

  function renderDrawer(c) {
    var itemsEl = drawerEl('cartDrawerItems');
    var subtotalEl = drawerEl('cartSubtotal');
    var checkoutBtn = drawerEl('cartCheckoutBtn');
    if (!itemsEl || !subtotalEl) return;

    var items = (c && c.items) || [];

    if (items.length === 0) {
      itemsEl.innerHTML = '<p class="cart-drawer-empty">Your cart is empty.</p>';
      if (checkoutBtn) checkoutBtn.disabled = true;
    } else {
      itemsEl.innerHTML = items.map(function (item) {
        var img = thumbUrl(item.image);
        var variant = item.variant_title && item.variant_title !== 'Default Title'
          ? '<div class="cart-line-meta">' + escapeHtml(item.variant_title) + '</div>'
          : '';
        var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
          ? '<div class="cart-line-meta">' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + '</div>'
          : '';
        variant += plan;
        return (
          '<div class="cart-line-item" data-key="' + escapeHtml(item.key) + '">' +
            (img ? '<img class="cart-line-img" src="' + escapeHtml(img) + '" alt="" loading="lazy">' : '') +
            '<div class="cart-line-info">' +
              '<div class="cart-line-name">' + escapeHtml(item.product_title) + '</div>' +
              variant +
              '<div class="cart-line-meta">' + money(item.final_line_price) + '</div>' +
            '</div>' +
            '<div class="cart-line-controls">' +
              '<button class="cart-line-qty-btn" data-action="minus" type="button" aria-label="Decrease quantity">&minus;</button>' +
              '<span class="cart-line-qty">' + item.quantity + '</span>' +
              '<button class="cart-line-qty-btn" data-action="plus" type="button" aria-label="Increase quantity">+</button>' +
              '<button class="cart-line-remove" data-action="remove" type="button">Remove</button>' +
            '</div>' +
          '</div>'
        );
      }).join('');
      if (checkoutBtn) checkoutBtn.disabled = false;
    }

    subtotalEl.textContent = money((c && c.items_subtotal_price) || 0);
  }

  function findItem(key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) return items[i];
    }
    return null;
  }

  function wireDrawer() {
    var itemsEl = drawerEl('cartDrawerItems');
    var closeBtn = drawerEl('cartDrawerClose');
    var backdrop = drawerEl('cartDrawerBackdrop');
    var checkoutBtn = drawerEl('cartCheckoutBtn');

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (backdrop) backdrop.addEventListener('click', closeDrawer);
    if (checkoutBtn) checkoutBtn.addEventListener('click', checkout);

    if (itemsEl) {
      // One delegated listener; rows are re-rendered on every cart change.
      itemsEl.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-action]') : null;
        if (!btn) return;
        var row = btn.closest('.cart-line-item');
        if (!row) return;
        var key = row.getAttribute('data-key');
        var item = findItem(key);
        if (!item) return;

        var action = btn.getAttribute('data-action');
        if (action === 'minus') {
          updateQuantity(key, item.quantity - 1).catch(function () {});
        } else if (action === 'plus') {
          updateQuantity(key, item.quantity + 1).catch(function () {});
        } else if (action === 'remove') {
          removeItem(key).catch(function () {});
        }
      });
    }
  }

  /* ------------------------------ public ------------------------------ */

  window.ChiflesCart = {
    addItem: addItem,
    updateQuantity: updateQuantity,
    removeItem: removeItem,
    clear: clear,
    getCart: getCart,
    // Sync conveniences over the last known server cart:
    getItems: function () { return cart ? cart.items.slice() : []; },
    getTotalCount: function () { return cart ? cart.item_count : 0; },
    getSubtotal: function () { return cart ? cart.items_subtotal_price / 100 : 0; },
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    checkout: checkout
  };

  /* ------------------------------- init ------------------------------- */

  function init() {
    wireDrawer();
    // Hydrate badge + drawer from the server cart on load.
    refresh().catch(function (err) {
      console.warn('[chifles cart] initial cart fetch failed:', err.message);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
